(ns ui.view.card.property-list.view
  (:require
   [common.localization :as loc]
   [common.schema.field :as field]
   [common.util :as cu]
   [frontend.view.entity :as f-entity]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.modal.style :as modal-style]
   [ui.components.modal.view :as modal]
   [ui.components.rich-text.tiptap :as tiptap]
   [ui.components.skeleton.view :as skeleton]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.util :as uu]
   [ui.view.card.property-list.event :as event]
   [ui.view.card.property-list.sub :as sub]
   [ui.view.card.shared-components.card-container :as card-container]
   [ui.view.card.shared-components.card-title :as card-title]
   [ui.view.card.shared-components.full-screen-card :as full-screen-card]
   [ui.view.card.shared-components.view :as card-shared]
   [ui.view.card.sub :as common-sub]))

(defn loading-skeleton
  []
  [:div
   {:class "flex flex-col gap-7"}
   [:div
    {:class "grid grid-cols-4"}
    [:div
     {:class "flex flex-col gap-1"}
     [skeleton/view
      {:class "h-5 w-24"}]
     [skeleton/view
      {:class "h-5 w-full"}]]
    [:div]
    [:div
     {:class "flex flex-col gap-1"}
     [skeleton/view
      {:class "h-5 w-24"}]
     [skeleton/view
      {:class "h-5 w-full"}]]]

   [:div
    {:class "grid grid-cols-4"}
    [:div
     {:class "flex flex-col gap-1 col-span-3"}
     [skeleton/view
      {:class "h-5 w-24"}]
     [skeleton/view
      {:class "h-5 w-96"}]
     [skeleton/view
      {:class "h-5 w-full"}]
     [skeleton/view
      {:class "h-5 w-56"}]]]])

(defn field-editor-component
  [{:keys [cell-data ctx] :as _props}]
  (let [{:keys [field field-schema
                value field-label
                data-type options write-access?
                materialized-value can-create-option-on-fly?
                secondary-field-data pipeline-id]} cell-data
        on-change (fn [{:keys [field value value-option-or-options]}]
                    (rf/dispatch-sync [::event/edit-field-value
                                       ctx
                                       {:field                   field
                                        :value                   value
                                        :value-option-or-options value-option-or-options}]))]
    [labelled-input/view
     (merge
      field-schema
      {:options options}
      ;; Without the pipeline, an option that is archived in it is offered.
      {:creation-pipeline-id pipeline-id}
      {:label       field-label
       :value       (when value
                      (case data-type
                        :data.type/ref-single {:value value
                                               :label (str materialized-value "")}
                        :data.type/ref-multi materialized-value
                        value))
       :editor-type (case data-type
                      :data.type/ref-single :editor.type/search
                      :data.type/ref-multi :editor.type/search
                      :data.type/currency-value :editor.type/currency-combo
                      nil)
       :data-type   data-type
       :disabled?   (not write-access?)
       :on-change   (fn [value value-option-or-options]
                      (on-change {:field                   field
                                  :value                   value
                                  :value-option-or-options value-option-or-options}))
       :create!     (when can-create-option-on-fly?
                      (fn [query]
                        (f-entity/open-creator!
                         (field/create-entity-types field-schema)
                         {:named-value-attribute       (:named-value-attribute field-schema)
                          :start-value                 query
                          :attribute                   (:attribute field-schema)
                          :skip-success-msg?           true
                          :creation-pipeline-id        pipeline-id
                          :dispatch-on-create-nev-2024 [::event/create-option-success ctx cell-data]
                          :on-ok!                      (fn [{:keys [close-cb!]}]
                                                         (close-cb!))})))}
      (when (= data-type :data.type/currency-value)
        {:secondary-props (merge secondary-field-data
                                 {:disabled? (-> secondary-field-data
                                                 :write-access?
                                                 not)
                                  :on-change (fn [value]
                                               (on-change {:field (:field secondary-field-data)
                                                           :value value}))})}))]))

(defn field-display-component
  [{:keys [cell-data]}]
  (let [field (:field cell-data)
        empty-cell-value (:empty-cell-value cell-data)
        field-label (:field-label cell-data)
        materialized-value (:materialized-value cell-data)
        data-type (:data-type cell-data)]
    [:div
     [:div
      {:class "text-neutral-600 font-medium text-sm m-0"}
      field-label]
     (if (and (cu/empty-value? materialized-value)
              (not (cu/empty-value? empty-cell-value)))
       [:div empty-cell-value]
       [:div
        [:<>
         (cond
           (or (= data-type :data.type/ref-multi)
               (= data-type :data.type/keyword-multi))
           [:div
            {:class "flex gap-1 flex-wrap"}
            (->> materialized-value
                 (map (fn [{:keys [value label]}]
                        ^{:key (str "value-" value)}
                        [:div
                         {:class "px-3 py-1 text-xs m-0 bg-base-regular text-base-regular-text rounded whitespace-nowrap"}
                         label])))]

           ;; TODO: handle these special cases somewhere
           (= field :common/named-network-drives)
           [:div
            {:class "flex flex-col gap-1"}
            (->> materialized-value
                 (map-indexed
                  (fn [index {:keys [value label]}]
                    ^{:key (str value "-" index)}
                    [:a
                     {:href   value
                      :target "_blank"
                      :class  "text-sm m-0 text-base-light-text-accent underline decoration-transparent transition-all hover:text-base-gloomy-text-accent hover:decoration-base-gloomy-text-accent"}
                     label])))]

           (= data-type :data.type/text-simple)
           [:p
            {:class "text-sm m-0"}
            materialized-value]

           (= data-type :data.type/text-multiline)
           [:div
            ;; HACK: Fix border and too much padding on rich text viewer
            {:class "[&_.ProseMirror]:p-0 [&_.ProseMirror]:border-0"}
            [tiptap/viewer
             {:value (or materialized-value "")}]]

           (= data-type :data.type/text-url)
           [:a
            {:href   materialized-value
             :class  "text-sm m-0 text-base-light-text-accent underline decoration-transparent transition-all hover:text-base-gloomy-text-accent hover:decoration-base-gloomy-text-accent"
             :target "_blank"}
            materialized-value]

           :else
           [:p
            {:class "text-sm m-0"}
            (str materialized-value)])]])]))


(defn row-cell-component
  [{:keys [cell-index row-index editor? ctx] :as props}]
  (let [card (:card ctx)
        cell-config (get-in card [:card/config :rows row-index :cells cell-index])
        {:keys [field always-visible?]} cell-config
        cell-data @(rf/subscribe [::sub/cell-data ctx {:field           field
                                                       :always-visible? always-visible?
                                                       :editor?         editor?}])
        hidden? (:hidden? cell-data)
        props (assoc props :cell-data cell-data)]
    [:div
     {:class (uu/cm "relative max-w-full overflow-hidden"
                    (when-not hidden?
                      ""))}
     (when-not hidden?
       (if editor?
         [field-editor-component props]
         [field-display-component props]))]))

(defn card-row-component
  [{:keys [row row-index] :as props}]
  (let [row-cells (:cells row)]
    [:div
     {:class "flex flex-row gap-2 md:gap-4"}
     (->> row-cells
          (map-indexed
           (fn [cell-index cell]
             (let [cell-width (:width cell)]
               ^{:key (str "r-" row-index "c" cell-index)}
               [:div
                {:class (case cell-width
                          1 "basis-full md:w-4/12"
                          2 "basis-full md:w-4/12"
                          3 "basis-full md:w-3/4"
                          4 "basis-full"
                          "basis-auto")}
                [row-cell-component
                 (merge
                  props
                  {:cell       cell
                   :cell-index cell-index})]]))))]))

(defn full-width-field-editor
  [{:keys [field ctx] :as props}]
  (let [cell-data @(rf/subscribe [::sub/cell-data ctx {:field           field
                                                       :editor?         true
                                                       :always-visible? true}])]
    [field-editor-component (assoc props :cell-data cell-data)]))

(defn show-all-fields-editor
  [{:keys [ctx] :as props}]
  (let [entity-type (-> ctx :card :card/entity-type)
        show-all-fields-in-editor? @(rf/subscribe [::sub/show-all-fields-in-editor? ctx])
        fields @(rf/subscribe [::sub/all-editor-fields ctx])]
    [:div
     {:class "flex flex-col gap-3"}
     [:div
      ;; TODO: Replace with sm primary open button when implemented
      [button/helper-button
       {:on-click #(rf/dispatch [::event/toggle-show-all-fields-in-editor ctx])}
       [:span
        (if show-all-fields-in-editor?
          (loc/tr :hide)
          (loc/tr :expand-entity-type-fields [(-> entity-type loc/tr cu/str-trim-lcase)]))
        [:i
         {:class (uu/cm "fal fa-angle-down no-underline"
                        (when show-all-fields-in-editor?
                          "-rotate-180"))}]]]]
     (when show-all-fields-in-editor?
       [:div
        {:class "flex flex-col gap-3"}
        (->> fields
             (map (fn [field]
                    ^{:key (str field)}
                    [:div
                     [full-width-field-editor (assoc props :field field)]])))])]))

(defn card-content-grid
  [{:keys [card ctx editor?]
    :as   props}]
  (let [card-rows (-> card
                      :card/config
                      :rows)
        submit-errors @(rf/subscribe [::sub/submit-errors ctx])]
    [:div
     {:class "flex flex-col gap-3"}
     (->> card-rows
          (map-indexed
           (fn [row-index row]
             ^{:key (str "card-row-" row-index)}
             [card-row-component
              (merge
               props
               {:row       row
                :row-index row-index})])))

     (when editor?
       [:div
        {:class "mt-4"}
        [show-all-fields-editor props]])

     (when (and editor? (seq submit-errors))
       (->> submit-errors
            (map-indexed (fn [idx error-msg]
                           ^{:key (str "submit-error-" idx)}
                           [:p
                            {:class "text-danger"}
                            error-msg]))))]))

(defn card-header-tools
  [{:keys [toggle-edit-mode] :as props}]
  [:div
   {:class "flex gap-2 opacity-0 group-hover/card:opacity-100 transition-opacity"}
   [card-shared/edit-mode
    {:on-click toggle-edit-mode}]
   [card-shared/expand-card props]])

(defn property-list-card
  [_props]
  (let [card-ref-atom (r/atom nil)]
    (fn [{:keys
          [card preview-mode? loading?]
          :as props}]
      (let [title (:card/title card)]
        [card-container/view
         {:ref (fn [ref]
                 (reset! card-ref-atom ref))}
         [:div
          {:class "flex flex-col gap-2 pb-4"}
          [:div
           {:class "flex justify-between items-center"}
           [card-title/view title]
           (when-not preview-mode?
             [card-header-tools
              (merge
               props
               {:card-ref-atom card-ref-atom})])]

          (cond
            loading? [loading-skeleton]
            :else [card-content-grid props])]]))))

(defn full-screen-modal-component
  [{:keys [card preview-mode? loading? edit-mode?] :as props}]
  (let [title (:card/title card)]
    [full-screen-card/view
     (merge
      props
      {:title             title
       :can-edit-content? (not preview-mode?)})
     (cond
       loading? [loading-skeleton]
       :else [card-content-grid (assoc props :editor? edit-mode?)])]))

(defn editor-modal
  [{:keys [edit-mode? card unsaved-changes?
           saving-changes? save-edits discard-edits] :as props}]
  [modal/view
   {:visible? edit-mode?
    :size     modal-style/size-lg}
   [:div (:card/title card)]
   [card-content-grid (assoc props :editor? true)]
   [:div
    {:class "flex gap-2 w-full items-center justify-end"}
    [button/sm-secondary-hollow
     {:on-click discard-edits}
     (loc/tr :cancel)]
    [button/sm-primary-solid
     {:on-click  save-edits
      :disabled? (or saving-changes?
                     (not unsaved-changes?))}
     (loc/tr :save)]]])

(defn view
  [{:keys [full-screen? ctx] :as props}]
  (let [edit-mode? @(rf/subscribe [::sub/edit-mode? ctx])
        toggle-edit-mode (fn []
                           (rf/dispatch [::event/toggle-edit-mode ctx]))

        loading? @(rf/subscribe [::common-sub/loading? ctx])
        discard-edits #(rf/dispatch [::event/discard-edits ctx])
        save-edits #(rf/dispatch [::event/save-changes! ctx])
        unsaved-changes? @(rf/subscribe [::sub/unsaved-changes? ctx])
        saving-changes? @(rf/subscribe [::sub/saving-changes? ctx])

        props (merge
               props
               {:edit-mode?       edit-mode?
                :loading?         loading?
                :toggle-edit-mode toggle-edit-mode
                :discard-edits    discard-edits
                :save-edits       save-edits
                :saving-changes?  saving-changes?
                :unsaved-changes? unsaved-changes?})]
    [:<>
     (if full-screen?
       [full-screen-modal-component props]
       [property-list-card props])
     (when (and edit-mode? (not full-screen?))
       [editor-modal props])]))
